import { useState, useEffect, useCallback } from 'react';
import { COOKIE_SETTINGS_EVENT } from '../content/legal';

// Cookie / analytics consent. Inside the EU/EEA/UK/CH Google Analytics 4 (and
// Microsoft Clarity, which follows the same decision) loads
// ONLY after the user explicitly accepts — nothing analytics-related runs before
// that. Outside it analytics are on by default (see isConsentRequired). With
// the GA or Clarity id emptied, that one never loads regardless of consent.
const CONSENT_KEY = 'cookieConsent_v1';
// Issued 2026-08-29 (L7). A GA4 measurement id is not a secret — it ships in
// the client bundle by design and identifies the property, it does not grant
// access to it — so it lives here rather than in an environment variable.
const GA_MEASUREMENT_ID = 'G-JFFHP7S2J4';

// Where a consent banner is legally required: EU/EEA, plus the UK and
// Switzerland, whose rules are GDPR-equivalent. Everywhere else analytics are on
// by default and the banner never shows — the footer's "Cookie settings" still
// opens it, so anyone can opt out.
//
// Detected from the browser's timezone: synchronous (no banner flash, no
// request), no backend, no IP lookup. It is deliberately over-inclusive — every
// Europe/* zone counts, so Serbia or Turkey see the banner too. A timezone that
// cannot be read counts as outside the region (analytics on, no banner) — a
// product decision from 2026-09-30, not an oversight.
//
// If you change this, update the "Cookies and analytics" section of
// public/privacy.html to match.
const CONSENT_TIMEZONES_OUTSIDE_EUROPE = new Set([
  'Asia/Nicosia', 'Asia/Famagusta',                      // Cyprus
  'Atlantic/Canary', 'Africa/Ceuta',                     // Spain
  'Atlantic/Madeira', 'Atlantic/Azores',                 // Portugal
  'Atlantic/Reykjavik',                                  // Iceland (EEA)
  'Arctic/Longyearbyen',                                 // Norway (EEA)
  'Atlantic/Faroe', 'America/Nuuk', 'America/Godthab',   // Denmark
  'America/Guadeloupe', 'America/Martinique', 'America/Cayenne',
  'America/Marigot', 'America/St_Barthelemy',
  'Indian/Reunion', 'Indian/Mayotte',                    // France, outermost regions
  'Atlantic/St_Helena', 'Atlantic/Stanley', 'Europe/Gibraltar', // UK-linked
]);

export const isConsentRequired = (timeZone) => {
  if (!timeZone) return false;
  return timeZone.startsWith('Europe/') || CONSENT_TIMEZONES_OUTSIDE_EUROPE.has(timeZone);
};

const detectTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
};
const CONSENT_REQUIRED = isConsentRequired(detectTimeZone());

// An explicit choice always wins. With none stored, a Global Privacy Control
// signal counts as a rejection, everywhere (California requires honouring it;
// splitting by region isn't worth it). Otherwise the default depends on the
// region: undecided (banner) where consent is required, accepted elsewhere. The
// implied defaults are NOT written to storage, so a visitor who later travels
// into the EU, or turns GPC off, gets the right default then.
export const readConsent = () => {
  let stored = null;
  try { stored = localStorage.getItem(CONSENT_KEY); } catch {}
  if (stored) return stored;
  if (typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true) return 'rejected';
  return CONSENT_REQUIRED ? null : 'accepted';
};

const GA_DEBUG_KEY = 'fiwara_gaDebug';

/**
 * GA4 DebugView only shows hits that carry `debug_mode`, and nothing in a normal
 * build sets it. Without this, the funnel can be working perfectly and DebugView
 * stays stubbornly empty — which reads as "analytics is broken" and is not.
 *
 * Opt in with `?ga_debug=1`; `?ga_debug=0` turns it back off; localhost is always
 * on. The choice is kept in sessionStorage on purpose: the checkout round-trip
 * leaves the site and returns on a URL whose query string Stripe controls, and
 * `checkout_success_return` / `plan_activated` are precisely the two events that
 * can only be observed across that hop.
 *
 * NEVER default this to true — every real visitor would land in DebugView.
 */
const readDebugFlag = () => {
  try {
    const flag = new URLSearchParams(window.location.search).get('ga_debug');
    if (flag === '1') { sessionStorage.setItem(GA_DEBUG_KEY, '1'); return true; }
    if (flag === '0') { sessionStorage.removeItem(GA_DEBUG_KEY); return false; }
    if (sessionStorage.getItem(GA_DEBUG_KEY) === '1') return true;
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  } catch {
    return false;
  }
};
const GA_DEBUG = typeof window !== 'undefined' ? readDebugFlag() : false;

const setDisabled = (disabled) => {
  if (GA_MEASUREMENT_ID) window[`ga-disable-${GA_MEASUREMENT_ID}`] = disabled;
};

let gaScriptLoaded = false;
const loadAnalytics = () => {
  if (!GA_MEASUREMENT_ID) return;

  // RE-ENABLING MATTERS AS MUCH AS LOADING, and this line has to sit before the
  // already-loaded guard rather than after it.
  //
  // Fixed 2026-09-06. The guard used to be `if (gaLoaded) return;` with the
  // re-enable below it, so this sequence broke analytics silently:
  //
  //   Accept  → script loads, ga-disable = false
  //   Cookie settings → Reject → ga-disable = true
  //   Accept again → early return, ga-disable STAYS TRUE
  //
  // Every hit is then dropped for the rest of the page session with no error
  // anywhere — and toggling consent back and forth is exactly what somebody
  // does while trying to see events arrive. It cost an evening of "why is
  // DebugView empty".
  setDisabled(false);
  if (gaScriptLoaded) return;
  gaScriptLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  // An ad blocker or a privacy-hardened browser blocks googletagmanager.com
  // outright. Say so once, rather than leaving it indistinguishable from a
  // consent problem or a wiring problem.
  script.onerror = () => {
    if (GA_DEBUG) console.warn('[ga] googletagmanager.com failed to load — blocked by an extension or the network?');
  };
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    ...(GA_DEBUG ? { debug_mode: true } : {}),
  });

  if (GA_DEBUG) {
    console.info(`[ga] loaded ${GA_MEASUREMENT_ID} with debug_mode — events should appear in GA4 DebugView`);
    // One call, in the console, that answers "why am I not seeing events".
    window.fiwaraGA = () => ({
      measurementId: GA_MEASUREMENT_ID,
      consent: readConsent(),
      gtagPresent: typeof window.gtag === 'function',
      scriptBlocked: !document.querySelector(`script[src*="${GA_MEASUREMENT_ID}"]`),
      disabled: Boolean(window[`ga-disable-${GA_MEASUREMENT_ID}`]),
      debugMode: GA_DEBUG,
    });
  }
};

const disableAnalytics = () => setDisabled(true);

// Microsoft Clarity (heatmaps + session recordings). Gated exactly like GA4: it
// loads on the same decision — an explicit accept, or the non-EU default — and
// never before. Like the GA id, the project id is public by design.
//
// Masking: <body data-clarity-mask="true"> in public/index.html masks all text,
// and the public landing pages opt back out with data-clarity-unmask. Keep the
// Clarity dashboard (Settings → Masking) on "Strict" as well, as a backstop:
// the app renders people's net worth and plan figures as plain text.
const CLARITY_PROJECT_ID = 'yqgd7h87hj';
const CLARITY_GRANTED = { ad_Storage: 'denied', analytics_Storage: 'granted' };
const CLARITY_DENIED = { ad_Storage: 'denied', analytics_Storage: 'denied' };

let clarityScriptLoaded = false;
let clarityStopped = false;
const loadClarity = () => {
  if (!CLARITY_PROJECT_ID) return;
  if (!clarityScriptLoaded) {
    clarityScriptLoaded = true;
    // Microsoft's snippet, unrolled: a queueing stub so calls made before the
    // script arrives are replayed once it does.
    window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
    script.onerror = () => {
      if (GA_DEBUG) console.warn('[clarity] clarity.ms failed to load — blocked by an extension or the network?');
    };
    document.head.appendChild(script);
  }
  // Same trap as GA's ga-disable above: after Reject → Accept in one page
  // session, recording has to be restarted, not just "already loaded".
  if (clarityStopped) { window.clarity('start'); clarityStopped = false; }
  window.clarity('consentv2', CLARITY_GRANTED);
};

const disableClarity = () => {
  // Never loaded → nothing to stop, and no reason to inject the stub.
  if (!clarityScriptLoaded) return;
  // consentv2 denied makes Clarity drop its cookies; stop ends the recording.
  window.clarity('consentv2', CLARITY_DENIED);
  window.clarity('stop');
  clarityStopped = true;
};

/**
 * Apply a consent decision. Exported because this — not the React plumbing — is
 * where every consequence lives, and it is worth testing without rendering
 * anything: the hook below is a thin wrapper over this one call.
 */
export const applyConsent = (consent) => {
  if (consent === 'accepted') { loadAnalytics(); loadClarity(); }
  else if (consent === 'rejected') { disableAnalytics(); disableClarity(); }
};

// Apply the stored (or default) decision as soon as this module loads, before
// React renders. The hook applies it again in an effect, but App's own mount
// effects run before that one (CookieConsent is a later sibling), so without
// this an event like checkout_success_return found no gtag and was dropped.
// Re-applying is idempotent.
if (typeof window !== 'undefined') applyConsent(readConsent());

// Funnel event logging. Consent-gated by construction: window.gtag only
// exists after loadAnalytics() ran (explicit accept, or the non-EU default), and a
// later rejection sets the ga-disable flag, which drops all hits. Without
// analytics it is a silent no-op, so call sites can be wired up
// unconditionally.
//
// Silent is right in production and useless while verifying the funnel, so in
// debug mode every outcome says which of the three it was: not loaded, disabled,
// or sent.
// Funnel events used across the app: demo_start, signup, activation,
// gate_hit{gate}, paywall_open{source}, checkout_click{plan},
// checkout_success_return, plan_activated, pricing_view.
export const trackEvent = (eventName, params = {}) => {
  try {
    if (!GA_MEASUREMENT_ID) return;
    if (typeof window.gtag !== 'function') {
      if (GA_DEBUG) console.warn(`[ga] "${eventName}" NOT sent — analytics never loaded. Accept the cookie banner.`, params);
      return;
    }
    if (window[`ga-disable-${GA_MEASUREMENT_ID}`]) {
      if (GA_DEBUG) console.warn(`[ga] "${eventName}" suppressed — ga-disable is set (consent rejected).`, params);
      return;
    }
    window.gtag('event', eventName, params);
    if (GA_DEBUG) console.info(`[ga] ${eventName}`, params);
  } catch { /* analytics must never break the app */ }
};

// consent is 'accepted' | 'rejected' | null (undecided → banner shows). Outside
// the consent region it starts as 'accepted', so the banner never appears unless
// reopened from the footer.
export const useConsent = () => {
  const [consent, setConsent] = useState(readConsent);

  useEffect(() => { applyConsent(consent); }, [consent]);

  // Footer "Cookie settings" re-opens the banner by clearing the decision in
  // state only (stored choice is left intact until the user re-decides).
  useEffect(() => {
    const reopen = () => setConsent(null);
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  const accept = useCallback(() => {
    try { localStorage.setItem(CONSENT_KEY, 'accepted'); } catch {}
    setConsent('accepted');
  }, []);

  const reject = useCallback(() => {
    try { localStorage.setItem(CONSENT_KEY, 'rejected'); } catch {}
    setConsent('rejected');
  }, []);

  return { consent, accept, reject };
};
